import { useEffect, useMemo, useRef } from "react";
import { p2Telemetry } from "../services/p2Telemetry.js";
import { runStore } from "../store/runStore.js";

/**
 * Hands the app state the P2 telemetry reads to services/p2Telemetry.js: the `tab.start`
 * snapshot's engine, as-of date, loaded (owned and shared) and selected forecasts and debug
 * overrides, and the forecasts with a run in progress (the attention tracker's streaming
 * state). The state is registered only once `ready` (both forecast lanes loaded) and
 * `isHydrated()` (the URL's compare / deep-link view applied), so the one-shot `tab.start` never
 * records a half-booted tab; the selected forecast is read live from the run store, which
 * those effects update synchronously. The service reads a ref, so registering never
 * re-renders; a change in the running set tells it.
 *
 * @param {{ready: boolean, isHydrated: () => boolean, active: object|null, forecasts: object[], sharedForecasts: object[],
 *   forecastEngine: string|null, mockToday: string, debugOverrides: object}} deps
 */
export function useTelemetryAppState({
  ready,
  isHydrated,
  active,
  forecasts,
  sharedForecasts,
  forecastEngine,
  mockToday,
  debugOverrides,
}) {
  const all = useMemo(
    () => [...(forecasts || []), ...(sharedForecasts || [])],
    [forecasts, sharedForecasts],
  );
  const streamingKey = useMemo(
    () =>
      all
        // A running branch streams on its root's surfaces (chat, timeline, row).
        .filter((f) => (f?.status === "running" || f?._branchRunning) && f.id)
        .map((f) => f.id)
        .sort()
        .join("\n"),
    [all],
  );
  const stateRef = useRef(null);
  // After every render: the service reads the latest committed state.
  useEffect(() => {
    stateRef.current = {
      engine: forecastEngine ?? null,
      asOf: mockToday || null,
      forecasts: all,
      selectedId: runStore.getState().activeRunId ?? active?.id ?? null,
      debugOverrides,
      streamingIds: new Set(streamingKey ? streamingKey.split("\n") : []),
    };
  });
  // Checked after every render, after the App's URL-apply effects (declared above this hook's
  // call) have run in the same flush.
  const registeredRef = useRef(false);
  useEffect(() => {
    if (registeredRef.current || !ready || !isHydrated?.()) return;
    registeredRef.current = true;
    p2Telemetry().setAppState(() => ({
      ...stateRef.current,
      selectedId: runStore.getState().activeRunId ?? stateRef.current?.selectedId ?? null,
    }));
  });
  useEffect(
    () => () => {
      registeredRef.current = false;
      p2Telemetry().setAppState(null);
    },
    [],
  );
  useEffect(() => {
    p2Telemetry().contextChanged();
  }, [streamingKey]);
}
